import { useCallback, useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import {
  checkHealthAsync,
  decodeToken,
  getCurrentCompany,
  loadCompanyAsync,
  removeToken,
  selectCompany,
  selectCurrentCompanyStatus,
  selectCurrentCompanyUuid,
  selectHealthCheckStatus,
  selectIsTokenDecoded,
  selectManager,
  homeSlice,
  setCurrentCompany,
  selectLoadCompanyStatus,
  selectDecodedTokenStatus,
  removeCurrentCompany,
} from "./routes/home/homeSlice";
import { colors } from "@qivia/ui";
import {
  managersListAsync,
  selectManagersListStatus,
} from "./routes/home/users/usersSlice";
import {
  selectAllCompanies,
  selectAllCompaniesStatus,
} from "./routes/home/companies/companiesSlice";

export const Layout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const company = useAppSelector(selectCompany);
  const companies = useAppSelector(selectAllCompanies);
  const location = useLocation();
  const isTokenDecoded = useAppSelector(selectIsTokenDecoded);
  const healthCheckStatus = useAppSelector(selectHealthCheckStatus);
  const currentCompany = useAppSelector(selectCurrentCompanyUuid);
  const currentCompanyStatus = useAppSelector(selectCurrentCompanyStatus);
  const allCompaniesStatus = useAppSelector(selectAllCompaniesStatus);
  const manager = useAppSelector(selectManager);
  const managersListStatus = useAppSelector(selectManagersListStatus);
  const loadCompanyStatus = useAppSelector(selectLoadCompanyStatus);
  const decodedTokenStatus = useAppSelector(selectDecodedTokenStatus);

  const isNotDepartmentManager = useMemo(
    () => manager && manager?.role !== "DEPARTMENT_USER",
    [manager],
  );

  //CHARGEMENT DU MANAGER TOKEN
  useEffect(() => {
    if (!isTokenDecoded) void dispatch(decodeToken());
  }, [dispatch, location.pathname, isTokenDecoded]);

  const logout = useCallback(() => {
    dispatch(removeToken());
    dispatch(removeCurrentCompany());
    navigate("/login");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (decodedTokenStatus === "failed") {
      logout();
    }
  }, [decodedTokenStatus, logout]);

  useEffect(() => {
    if (isTokenDecoded && manager && healthCheckStatus === "idle") {
      void dispatch(checkHealthAsync());
    }
  }, [healthCheckStatus, dispatch, manager, isTokenDecoded]);

  useEffect(() => {
    if (isTokenDecoded && !manager && location.pathname.includes("/home")) {
      dispatch(removeToken());
      navigate("/login");
    }
  }, [navigate, isTokenDecoded, dispatch, manager, location.pathname]);

  // Handle department user redirection
  useEffect(() => {
    if (manager && healthCheckStatus === "success") {
      // Check if user is a department user and redirect accordingly
      if (manager.role === "DEPARTMENT_USER") {
        if (
          location.pathname === "/" ||
          location.pathname.startsWith("/home")
        ) {
          navigate("/departments");
        }
      } else {
        // Regular users should not access department routes
        if (location.pathname.startsWith("/departments")) {
          navigate("/home");
        }
      }

      void dispatch(
        homeSlice.actions.storeManager({
          manager: manager,
        }),
      );
    }
  }, [dispatch, healthCheckStatus, manager, location.pathname, navigate]);

  //CHARGEMENT DES COMPANIES LIST ET DE LA COMPAGNIE ACTUELLE

  useEffect(() => {
    if (
      !company &&
      !currentCompany &&
      companies.length > 0 &&
      allCompaniesStatus === "success"
    ) {
      const companyUuid = companies[0].uuid;
      void dispatch(
        setCurrentCompany({
          companyUuid,
        }),
      );
    }
  }, [allCompaniesStatus, companies, company, currentCompany, dispatch]);

  //CHARGEMENT DE LA COMPAGNIE ACTUELLE SI STOCKE EN LOCAL
  useEffect(() => {
    if (!company && currentCompanyStatus === "idle") {
      void dispatch(getCurrentCompany());
    }
  }, [company, currentCompany, currentCompanyStatus, dispatch]);

  //CHARGEMENT DE LA COMPAGNIE ACTUELLE SI STOCKE EN LOCAL
  useEffect(() => {
    if (
      !company &&
      currentCompany &&
      currentCompanyStatus === "success" &&
      isNotDepartmentManager
    ) {
      void dispatch(loadCompanyAsync({ uuid: currentCompany }));
    }
  }, [
    company,
    currentCompany,
    currentCompanyStatus,
    dispatch,
    isNotDepartmentManager,
  ]);

  //CHARGEMENT DES MANAGERS LIST
  useEffect(() => {
    if (
      company &&
      managersListStatus === "idle" &&
      loadCompanyStatus === "success"
    ) {
      void dispatch(managersListAsync({ companyUuid: company.uuid }));
    }
  }, [dispatch, company, managersListStatus, loadCompanyStatus]);

  //CHARGEMENT DU MANAGER BACK

  useEffect(() => {
    if (
      manager &&
      (!manager.status ||
        !manager.role ||
        !manager.companyGroup ||
        manager.status === "DEACTIVATED")
    ) {
      dispatch(removeToken());
      navigate("/login");
    }
  }, [manager, dispatch, navigate]);

  if (location.pathname === "/") return <Navigate to="/home" />;

  return (
    <StyledContainer>
      <Outlet />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  width: 100%;
  height: 100vh;
  background-color: ${colors.background};
`;
